import React, { useState, useEffect } from 'react';
import { Card, Table, Select, Space, Button, message, Tag, Tooltip } from 'antd';
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { powerAPI, configAPI, historyAPI } from '../services/api';
import { AppSettings, PowerRankingItem, SelectOption, HistoryGranularity } from '../types';
import { compareSnapshotsDesc } from '../utils/snapshot';
import HistoryControls from './HistoryControls';

interface PowerRankingsProps {
  settings: AppSettings;
}

const PowerRankings: React.FC<PowerRankingsProps> = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PowerRankingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [characterId, setCharacterId] = useState<number | null>(null);
  const [characters, setCharacters] = useState<SelectOption[]>([]);
  // 历史对比相关
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [granularity, setGranularity] = useState<HistoryGranularity>('day');
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [snapshotFrom, setSnapshotFrom] = useState<string | undefined>(undefined);
  const [snapshotTo, setSnapshotTo] = useState<string | undefined>(undefined);

  // 加载角色列表
  useEffect(() => {
    const loadCharacters = async () => {
      try {
        const result = await configAPI.getCharacters();
        setCharacters(result);
      } catch (error) {
        console.error('加载角色列表失败:', error);
      }
    };
    loadCharacters();
  }, []);

  // 加载数据
  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      if (historyEnabled && snapshotFrom && snapshotTo) {
        // 历史对比模式：直接拿对比结果
        const res = await historyAPI.compare<PowerRankingItem>({
          category: 'power',
          granularity,
          snapshot_from: snapshotFrom,
          snapshot_to: snapshotTo,
          server_id: settings.serverId ?? undefined,
          character_id: characterId ?? undefined,
          page,
          page_size: settings.pageSize,
        });
        // 将对比结果映射为表格数据（以目标快照为主），兼容后端字段：old/new_* 与 from/to 结构
        const items = (res.diffs || []).map((d: any, index: number) => {
          const toItem = d.to || {};
          const fromItem = d.from || {};
          const newRank = (d.new_rank ?? toItem.global_rank ?? toItem.rank ?? 0) as number;
          const oldRank = (d.old_rank ?? fromItem.global_rank ?? fromItem.rank ?? 0) as number;
          const newScore = (d.new_score ?? toItem.score ?? 0) as number;
          const oldScore = (d.old_score ?? fromItem.score ?? 0) as number;

          const scoreDelta = (newScore - oldScore) as number;
          const rankDelta = (newRank - oldRank) as number;

          return {
            ...toItem,
            rank: newRank,
            formatted_score: toItem.formatted_score ?? (toItem.score != null ? String(toItem.score) : ''),
            formatted_fightpoint: toItem.formatted_fightpoint ?? (toItem.fightpoint != null ? String(toItem.fightpoint) : ''),
            score_delta: scoreDelta,
            rank_delta: rankDelta,
            is_new: !!d.is_new,
            filtered_rank: (page - 1) * settings.pageSize + index + 1,
          } as PowerRankingItem & { score_delta: number; rank_delta: number; is_new?: boolean };
        });
        setData(items);
        setTotal(res.total ?? items.length);
        setCurrentPage(res.page ?? page);
      } else {
        // 非对比模式：如选择了某个快照（snapshotTo），则读取该快照历史；否则读取当前实时榜
        if (snapshotTo) {
          const result = await historyAPI.getHistory<PowerRankingItem>({
            category: 'power',
            granularity,
            snapshot: snapshotTo,
            server_id: settings.serverId ?? undefined,
            character_id: characterId ?? undefined,
            page,
            page_size: settings.pageSize,
          });
          // 兼容历史快照字段命名：可能没有 rank/格式化字段
          let items = (result.items || []).map((item: any) => {
            const rank = (item.rank ?? item.global_rank ?? item.server_rank ?? 0) as number;
            const formatted_score = item.formatted_score ?? (item.score != null ? String(item.score) : '');
            const formatted_fightpoint = item.formatted_fightpoint ?? (item.fightpoint != null ? String(item.fightpoint) : '');
            return { ...item, rank, formatted_score, formatted_fightpoint } as PowerRankingItem;
          });
          // 历史模式已通过服务端筛选，无需前端过滤
          // 排序：若 rank 缺失，按 score 降序兜底
          items.sort((a: any, b: any) => {
            const ra = a.rank ?? 0, rb = b.rank ?? 0;
            if (ra && rb) return ra - rb;
            return (b.score ?? 0) - (a.score ?? 0);
          });
          const itemsWithFilteredRank = items.map((item, index) => ({
            ...item,
            filtered_rank: (page - 1) * settings.pageSize + index + 1,
          }));
          setData(itemsWithFilteredRank);
          setTotal(result.total ?? itemsWithFilteredRank.length);
          setCurrentPage(result.page ?? page);
        } else {
          const result = await powerAPI.getRankings({
            page,
            page_size: settings.pageSize,
            server_id: settings.serverId,
            character_id: characterId,
            use_units: settings.useUnits,
          });
  
          const itemsWithFilteredRank = result.items.map((item, index) => ({
            ...item,
            filtered_rank: (page - 1) * settings.pageSize + index + 1,
          }));
  
          setData(itemsWithFilteredRank);
          setTotal(result.total);
          setCurrentPage(page);
        }
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载和依赖更新
  useEffect(() => {
    // 列表或对比参数变化都触发加载
    loadData(1);
  }, [settings.pageSize, settings.serverId, settings.useUnits, characterId, historyEnabled, granularity, snapshotFrom, snapshotTo]);

  // 拉取快照列表（当粒度/服务器变化或开启对比时）
  useEffect(() => {
    const fetchSnapshots = async () => {
      // 非对比模式也需要快照列表，用于单选
      try {
        const resp = await historyAPI.listSnapshots({ category: 'power', granularity, limit: 60 });
  const snaps = (resp.snapshots || []).slice().sort(compareSnapshotsDesc);
        setSnapshots(snaps);
        if (historyEnabled) {
          if (snaps.length >= 2) {
            setSnapshotFrom(snaps[1]);
            setSnapshotTo(snaps[0]);
          }
        } else {
          if (snaps.length >= 1) {
            setSnapshotTo(snaps[0]);
          }
        }
      } catch (e) {
        console.error('加载快照失败', e);
      }
    };
    fetchSnapshots();
  }, [historyEnabled, granularity, settings.serverId]);

  const handleRefresh = () => {
    loadData(currentPage);
  };

  const handleCharacterChange = (value: number | null) => {
    setCharacterId(value);
    setCurrentPage(1);
  };

  const columns = [
    {
      title: '总榜排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <Tag color={rank <= 3 ? 'gold' : rank <= 10 ? 'orange' : 'default'}>
          {rank}
        </Tag>
      ),
    },
    {
      title: '筛选排名',
      dataIndex: 'filtered_rank',
      key: 'filtered_rank',
      width: 80,
      render: (rank: number) => (
        <Tag color="blue">{rank}</Tag>
      ),
    },
    {
      title: '玩家名称',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (name: string) => (
        <Tooltip title={name}>
          <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{name}</span>
        </Tooltip>
      ),
    },
    {
      title: '上榜战力',
      dataIndex: 'formatted_score',
      key: 'score',
      width: 100,
      render: (score: string) => (
        <span style={{ fontWeight: 'bold', color: '#f5222d' }}>{score}</span>
      ),
    },
    {
      title: '当前战力',
      dataIndex: 'formatted_fightpoint',
      key: 'fightpoint',
      width: 100,
      render: (fightpoint: string) => (
        <span style={{ color: '#fa8c16' }}>{fightpoint}</span>
      ),
    },
    {
      title: '等级',
      dataIndex: 'lv',
      key: 'lv',
      width: 70,
      render: (lv: number) => (
        <Tag color="green">{lv}</Tag>
      ),
    },
    {
      title: '角色',
      dataIndex: 'character_name',
      key: 'character_name',
      width: 100,
    },
    {
      title: '联盟',
      dataIndex: 'league_name',
      key: 'league_name',
      width: 120,
      render: (league: string) => league || '无',
    },
    {
      title: 'UID',
      dataIndex: 'uid',
      key: 'uid',
      width: 120,
      render: (uid: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{uid}</span>
      ),
    },
    {
      title: '地区',
      dataIndex: 'ip_area',
      key: 'ip_area',
      width: 100,
    },
    {
      title: '服务器',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 80,
      render: (serverId: number) => (
        <Tag color="purple">服务器{serverId}</Tag>
      ),
    },
    // 对比模式新增列：分数/排名变化
    ...(historyEnabled ? [
      {
        title: '战力变化',
        dataIndex: 'score_delta',
        key: 'score_delta',
        width: 110,
        align: 'center' as const,
        render: (delta: number) => (
          <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) > 0 ? '#52c41a' : '#f5222d', fontWeight: 500 }}>
            {delta > 0 ? `+${delta}` : `${delta || 0}`}
          </span>
        ),
      },
      {
        title: '排名变化',
        dataIndex: 'rank_delta',
        key: 'rank_delta',
        width: 120,
        align: 'center' as const,
        render: (delta: number, record: any) => {
          if (record?.is_new) {
            return <Tag color="green" style={{ fontWeight: 700 }}>新上榜</Tag>;
          }
          return (
            <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) < 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}>
              {delta < 0 ? `⬆ ${Math.abs(delta)}` : delta > 0 ? `⬇ ${delta}` : '—'}
            </span>
          );
        },
      },
    ] : []),
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <span>⚔️ 战力排行榜</span>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
              size="small"
            >
              刷新
            </Button>
          </Space>
        }
        extra={
          <Space>
            <HistoryControls
              enabled={historyEnabled}
              granularity={granularity}
              snapshots={snapshots}
              from={snapshotFrom}
              to={snapshotTo}
              onChange={(v) => {
                setHistoryEnabled(v.enabled);
                setGranularity(v.granularity);
                setSnapshotFrom(v.from);
                setSnapshotTo(v.to);
              }}
            />
            <FilterOutlined />
            <Select
              placeholder="选择角色"
              style={{ width: 120 }}
              value={characterId}
              onChange={handleCharacterChange}
              allowClear
            >
              {characters.map(char => (
                <Select.Option key={char.id} value={char.id}>
                  {char.name}
                </Select.Option>
              ))}
            </Select>
          </Space>
        }
      >
        <Table
          sticky
          className="ranking-table"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="uid"
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            pageSize: settings.pageSize,
            total: total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page) => loadData(page),
          }}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default PowerRankings;
